import { Directive, ElementRef, HostListener, inject } from '@angular/core';

@Directive({
    selector: '[tranCustomValueSelect]',
    standalone: true
})
export class CustomValueSelectDirective {
    private readonly el = inject(ElementRef<HTMLElement>);

    // ── Arrow up / down ──────────────────────────────────────────────────────

    @HostListener('keydown.arrowdown', ['$event'])
    @HostListener('keydown.arrowup', ['$event'])
    protected onPanelArrowKey(event: Event): void {
        event.preventDefault();
        const ke = event as KeyboardEvent;
        const options = Array.from(document.querySelectorAll<HTMLElement>('[data-option-btn]:not([disabled])'));
        if (!options.length) return;
        const dir = ke.key === 'ArrowDown' ? 1 : -1;
        const active = document.activeElement as HTMLElement | null;
        const currentOption = active?.matches('[data-option-btn]')
            ? active
            : active?.closest('.dropdown-item')?.querySelector<HTMLElement>('[data-option-btn]');
        const currentIdx = currentOption ? options.indexOf(currentOption) : -1;
        const nextIdx =
            currentIdx >= 0 ? (currentIdx + dir + options.length) % options.length : dir > 0 ? 0 : options.length - 1;
        this.focusPanelOption(options[nextIdx], dir);
    }

    // ── Arrow right (option → delete button) ────────────────────────────────

    @HostListener('keydown.arrowright', ['$event'])
    protected onPanelRightKey(event: Event): void {
        const active = document.activeElement as HTMLElement;
        if (!active?.matches('[data-option-btn]')) return;
        const deleteBtn = active
            .closest('.dropdown-item')
            ?.querySelector<HTMLElement>('[data-delete-btn]:not([disabled])');
        if (deleteBtn) {
            event.preventDefault();
            deleteBtn.focus();
        }
    }

    // ── Arrow left (delete button → option) ─────────────────────────────────

    @HostListener('keydown.arrowleft', ['$event'])
    protected onPanelLeftKey(event: Event): void {
        const active = document.activeElement as HTMLElement;
        if (!active?.matches('[data-delete-btn]')) return;
        event.preventDefault();
        const optionBtn = active.closest('.dropdown-item')?.querySelector<HTMLElement>('[data-option-btn]');
        optionBtn?.focus();
    }

    // ── Focus tracking (scroll into view) ───────────────────────────────────

    @HostListener('focusin', ['$event'])
    protected onPanelFocusIn(event: FocusEvent): void {
        const target = event.target;
        if (!(target instanceof HTMLElement)) return;

        // Guard: only react to focus events inside this panel element
        if (!this.el.nativeElement.contains(target)) return;

        const row = target.closest<HTMLElement>('.dropdown-item');
        if (row) {
            this.scrollViewportToElement(row);
            return;
        }

        this.scrollViewportToElement(target, this.getCustomFooterViewportPadding());
    }

    // ── Private helpers (verbatim from the component) ────────────────────────

    private focusPanelOption(option: HTMLElement | null | undefined, direction: -1 | 0 | 1): void {
        option?.focus();

        const row = option?.closest<HTMLElement>('.dropdown-item');
        const panel = option?.closest<HTMLElement>('[data-subject-dropdown]');

        if (row && panel) {
            this.scrollPanelToRow(row, panel, direction);
            this.scrollViewportToElement(row);
        }
    }

    private scrollPanelToRow(row: HTMLElement, panel: HTMLElement, _direction: -1 | 0 | 1): void {
        const rowRect = row.getBoundingClientRect();
        const panelRect = panel.getBoundingClientRect();
        const visiblePanelBottom = panelRect.bottom - this.getStickyFooterHeight();

        if (rowRect.bottom > visiblePanelBottom) {
            panel.scrollTop += rowRect.bottom - visiblePanelBottom;
            return;
        }

        if (rowRect.top < panelRect.top) {
            panel.scrollTop -= panelRect.top - rowRect.top;
        }
    }

    private scrollViewportToElement(element: HTMLElement, bottomPadding = 12): void {
        const topPadding = 12;
        const elementRect = element.getBoundingClientRect();

        if (elementRect.bottom > window.innerHeight - bottomPadding) {
            window.scrollBy({ top: elementRect.bottom - window.innerHeight + bottomPadding });
            return;
        }

        if (elementRect.top < topPadding) {
            window.scrollBy({ top: elementRect.top - topPadding });
        }
    }

    private getCustomFooterViewportPadding(): number {
        const basePadding = 12;
        return basePadding + this.getStickyFooterHeight();
    }

    private getStickyFooterHeight(): number {
        const footer = document.querySelector<HTMLElement>('[data-custom-footer]');
        return footer ? footer.getBoundingClientRect().height : 0;
    }
}
